"use client";

import React, { type ComponentType, type SVGProps } from "react";
import {
  BanknotesIcon,
  BuildingOffice2Icon,
  CreditCardIcon,
  IdentificationIcon,
  ServerStackIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const systemRows: { label: string; Icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
  { label: "ERP", Icon: BuildingOffice2Icon },
  { label: "CRM", Icon: UserGroupIcon },
  { label: "POS", Icon: CreditCardIcon },
  { label: "Finance Systems", Icon: BanknotesIcon },
  { label: "HRMS", Icon: IdentificationIcon },
  { label: "Legacy Systems", Icon: ServerStackIcon },
];

const HowSimaxSolvesSection = () => {
  return (
    <section className="bg-white py-12 sm:py-16 md:py-24 min-h-0 flex items-center">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Eyebrow */}
        <p className="text-gold text-xs sm:text-sm font-semibold uppercase tracking-[1.5px] text-left md:text-center mb-4 sm:mb-6">
          THE SOLUTION
        </p>

        {/* Headline */}
        <h2 className="font-serif text-navy text-[24px] sm:text-[32px] md:text-[40px] lg:text-[44px] leading-[1.2] font-bold text-left md:text-center mb-8 sm:mb-12 md:mb-16 max-w-5xl md:mx-auto">
          Enterprise Data Warehousing: One Source of Truth. Zero Guesswork.
        </h2>

        {/* Three-Column Layout Labels - stack on mobile */}
        <div className="max-w-[1400px] mx-auto mb-6 md:mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8 text-left lg:text-center">
            <div>
              <p className="text-navy text-base sm:text-lg font-bold">Multiple Systems</p>
            </div>
            <div>
              <p className="text-navy text-base sm:text-lg font-bold">Transformation</p>
            </div>
            <div>
              <p className="text-navy text-base sm:text-lg font-bold">Simax Solution</p>
            </div>
          </div>
        </div>

        {/* Visual Flow Diagram - stack on mobile */}
        <div className="max-w-[1400px] mx-auto mb-10 sm:mb-16 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-16 items-center">
            {/* LEFT: Multiple Systems with Chaotic Lines */}
            <div className="relative order-1 lg:order-none">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                {systemRows.map(({ label, Icon }) => (
                  <div
                    key={label}
                    className="bg-white border-2 border-navy rounded-lg px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 text-sm sm:text-base font-semibold text-navy shadow-md flex items-center gap-2 sm:gap-3"
                  >
                    <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-gold shrink-0" aria-hidden={true} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CENTER: Transformation Process */}
            <div className="flex flex-col items-center px-4 sm:px-8 lg:px-12 relative order-2 lg:order-none min-w-0 lg:min-w-[280px]">
              {/* Data Integration Standardization Text */}
              <p className="text-navy text-sm font-semibold text-center mb-6 tracking-wide">
                Data Integration<br/>Standardization
              </p>
              
              {/* Funnel Icon */}
              <svg width="80" height="60" viewBox="0 0 80 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4">
                <path d="M 10 5 L 70 5 L 50 35 L 30 35 Z" fill="#C5A572" opacity="0.3" stroke="#C5A572" strokeWidth="2"/>
                <path d="M 30 35 L 50 35 L 45 55 L 35 55 Z" fill="#C5A572" opacity="0.5" stroke="#C5A572" strokeWidth="2"/>
              </svg>
              
              {/* Downward Arrow */}
              <svg width="24" height="30" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-4">
                <path d="M 12 0 L 12 24 M 12 24 L 6 18 M 12 24 L 18 18" stroke="#C5A572" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              
              {/* 3D Globe Design — 2D rotate only so it stays locked to center (no rotateY drift) */}
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 mb-4 flex items-center justify-center shrink-0">
                <div className="w-full h-full origin-center animate-globe-in-place">
                {/* Globe SVG */}
                <svg className="w-full h-full block" viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Outer glow effect */}
                  <circle cx="64" cy="64" r="58" fill="url(#globeGlow)" opacity="0.3" />
                  
                  {/* Main globe sphere with gradient */}
                  <circle cx="64" cy="64" r="50" fill="url(#globeGradient)" />
                  
                  {/* Latitude lines */}
                  <ellipse cx="64" cy="64" rx="50" ry="12" stroke="#C5A572" strokeWidth="1" fill="none" opacity="0.6" />
                  <ellipse cx="64" cy="64" rx="50" ry="25" stroke="#C5A572" strokeWidth="1" fill="none" opacity="0.6" />
                  <ellipse cx="64" cy="64" rx="50" ry="38" stroke="#C5A572" strokeWidth="1" fill="none" opacity="0.6" />
                  
                  {/* Longitude lines */}
                  <ellipse cx="64" cy="64" rx="12" ry="50" stroke="#C5A572" strokeWidth="1" fill="none" opacity="0.6" />
                  <ellipse cx="64" cy="64" rx="25" ry="50" stroke="#C5A572" strokeWidth="1" fill="none" opacity="0.6" />
                  <ellipse cx="64" cy="64" rx="38" ry="50" stroke="#C5A572" strokeWidth="1" fill="none" opacity="0.6" />
                  
                  {/* Equator line (thicker) */}
                  <line x1="14" y1="64" x2="114" y2="64" stroke="#C5A572" strokeWidth="1.5" opacity="0.8" />
                  
                  {/* Prime meridian (thicker) */}
                  <line x1="64" y1="14" x2="64" y2="114" stroke="#C5A572" strokeWidth="1.5" opacity="0.8" />
                  
                  {/* Continental outlines (simplified) */}
                  {/* North America */}
                  <path d="M 35 35 Q 40 30, 45 35 Q 50 40, 45 45 Q 40 50, 35 45 Z" fill="#C5A572" opacity="0.4" />
                  
                  {/* Europe/Africa */}
                  <path d="M 70 40 Q 75 38, 78 42 Q 80 48, 75 52 Q 70 55, 68 50 Z" fill="#C5A572" opacity="0.4" />
                  
                  {/* Asia */}
                  <path d="M 85 30 Q 92 28, 95 35 Q 98 42, 92 48 Q 88 52, 85 45 Z" fill="#C5A572" opacity="0.4" />
                  
                  {/* 3D highlight effect */}
                  <circle cx="64" cy="64" r="50" fill="url(#globeHighlight)" opacity="0.3" />
                  
                  {/* Outer border */}
                  <circle cx="64" cy="64" r="50" stroke="#1B2845" strokeWidth="2" fill="none" />
                  
                  {/* Gradient definitions */}
                  <defs>
                    <radialGradient id="globeGradient" cx="0.35" cy="0.35">
                      <stop offset="0%" stopColor="#3B5998" />
                      <stop offset="50%" stopColor="#1B2845" />
                      <stop offset="100%" stopColor="#0F1729" />
                    </radialGradient>
                    <radialGradient id="globeHighlight" cx="0.3" cy="0.3">
                      <stop offset="0%" stopColor="#FFFFFF" />
                      <stop offset="50%" stopColor="#FFFFFF" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="globeGlow" cx="0.5" cy="0.5">
                      <stop offset="0%" stopColor="#C5A572" />
                      <stop offset="100%" stopColor="#C5A572" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                </svg>
                </div>
              </div>
              
              {/* Cleansing Enrichment Text */}
              <p className="text-navy text-sm font-semibold text-center tracking-wide">
                Cleansing<br/>Enrichment
              </p>
            </div>

            {/* RIGHT: SIMAX ENTERPRISE DATA WAREHOUSE */}
            <div className="relative order-3 lg:order-none">
              {/* Main Container */}
              <div className="bg-white border-4 border-navy rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-10 shadow-2xl">
                {/* Simax Logo and Title */}
                <div className="mb-5 sm:mb-6 md:mb-8">
                  <div className="flex items-center mb-3 sm:mb-4">
                    {/* Simax S Logo */}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br from-gold to-[#B8935A] flex items-center justify-center mr-2 sm:mr-3 shadow-md">
                      <span className="text-white font-bold text-xl sm:text-2xl italic">S</span>
                    </div>
                    <span className="text-navy font-bold text-lg sm:text-xl">Simax</span>
                  </div>
                  <h3 className="text-navy font-bold text-lg sm:text-xl md:text-2xl leading-tight">
                    SIMAX ENTERPRISE DATA WAREHOUSE
                  </h3>
                </div>
                
                {/* Top Row Icons - 4 items */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-5 sm:mb-6 md:mb-8">
                  {/* Analytics */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="16" cy="16" r="10" stroke="#C5A572" strokeWidth="2" fill="none"/>
                        <circle cx="16" cy="16" r="6" stroke="#C5A572" strokeWidth="2" fill="none"/>
                        <line x1="16" y1="6" x2="16" y2="10" stroke="#C5A572" strokeWidth="2"/>
                        <line x1="16" y1="22" x2="16" y2="26" stroke="#C5A572" strokeWidth="2"/>
                        <line x1="6" y1="16" x2="10" y2="16" stroke="#C5A572" strokeWidth="2"/>
                        <line x1="22" y1="16" x2="26" y2="16" stroke="#C5A572" strokeWidth="2"/>
                      </svg>
                    </div>
                    <p className="text-navy text-sm font-semibold">Analytics</p>
                  </div>
                  
                  {/* Reporting */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" y="20" width="5" height="8" fill="#C5A572"/>
                        <rect x="13" y="14" width="5" height="14" fill="#C5A572"/>
                        <rect x="20" y="8" width="5" height="20" fill="#C5A572"/>
                        <line x1="4" y1="28" x2="28" y2="28" stroke="#1B2845" strokeWidth="2"/>
                      </svg>
                    </div>
                    <p className="text-navy text-sm font-semibold">Reporting</p>
                  </div>
                  
                  {/* BI Dashboards */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="4" y="4" width="24" height="24" rx="2" stroke="#C5A572" strokeWidth="2" fill="none"/>
                        <rect x="8" y="8" width="6" height="6" fill="#C5A572"/>
                        <rect x="18" y="8" width="6" height="6" fill="#C5A572"/>
                        <rect x="8" y="18" width="6" height="6" fill="#C5A572"/>
                        <rect x="18" y="18" width="6" height="6" fill="#C5A572"/>
                      </svg>
                    </div>
                    <p className="text-navy text-sm font-semibold">BI Dashboards</p>
                  </div>
                  
                  {/* Single Source of Truth */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M 16 4 L 20 8 L 20 14 L 28 14 L 28 28 L 4 28 L 4 14 L 12 14 L 12 8 Z" fill="#C5A572" opacity="0.3" stroke="#C5A572" strokeWidth="2"/>
                        <path d="M 12 18 L 14 20 L 20 14" stroke="#C5A572" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="text-navy text-sm font-semibold leading-tight">Single Source 
of Truth</p>
                  </div>
                </div>
                
                {/* Divider line */}
                <div className="w-full h-px bg-gray-300 mb-5 sm:mb-6 md:mb-8"></div>
                
                {/* Bottom Row Icons - 4 items: 2x2 on mobile */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {/* Insights */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="14" cy="18" r="6" fill="#C5A572" opacity="0.3"/>
                        <path d="M 14 4 L 14 12" stroke="#C5A572" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M 14 12 L 10 8" stroke="#C5A572" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M 14 12 L 18 8" stroke="#C5A572" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <p className="text-navy text-xs font-semibold">Insights</p>
                  </div>
                  
                  {/* Growth */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M 4 24 L 10 18 L 14 20 L 24 8" stroke="#C5A572" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M 24 8 L 24 14 M 24 8 L 18 8" stroke="#C5A572" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="text-navy text-xs font-semibold">Growth</p>
                  </div>
                  
                  {/* Collaboration */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="10" cy="10" r="4" fill="#C5A572"/>
                        <circle cx="18" cy="10" r="4" fill="#C5A572"/>
                        <circle cx="14" cy="18" r="4" fill="#C5A572"/>
                        <path d="M 10 14 L 14 18 M 18 14 L 14 18" stroke="#C5A572" strokeWidth="2" opacity="0.5"/>
                      </svg>
                    </div>
                    <p className="text-navy text-xs font-semibold">Collaboration</p>
                  </div>
                  
                  {/* Scalability */}
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M 14 4 Q 20 8, 20 14 Q 20 20, 14 24 Q 8 20, 8 14 Q 8 8, 14 4 Z" fill="#C5A572" opacity="0.3" stroke="#C5A572" strokeWidth="2"/>
                        <circle cx="14" cy="8" r="1.5" fill="#C5A572"/>
                        <circle cx="10" cy="14" r="1.5" fill="#C5A572"/>
                        <circle cx="18" cy="14" r="1.5" fill="#C5A572"/>
                        <circle cx="14" cy="20" r="1.5" fill="#C5A572"/>
                      </svg>
                    </div>
                    <p className="text-navy text-xs font-semibold">Scalability</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* NEW CONNECTION LINES */}
          {/* Single line from all systems collectively to globe center */}
          <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{zIndex: 10}}>
            <defs>
              <marker id="arrowhead-new" markerWidth="4" markerHeight="4" refX="3.5" refY="2" orient="auto">
                <polygon points="0 0, 4 2, 0 4" fill="#C5A572" />
              </marker>
            </defs>
            
            {/* Single clean line from left systems area (collective) to globe center */}
            <line 
              x1="35%" 
              y1="50%" 
              x2="45%" 
              y2="50%" 
              stroke="#C5A572" 
              strokeWidth="1.5" 
              markerEnd="url(#arrowhead-new)" 
            />
            
            {/* Single straight line from globe center to Simax solution box */}
            <line 
              x1="55%" 
              y1="50%" 
              x2="65%" 
              y2="50%" 
              stroke="#C5A572" 
              strokeWidth="1.5" 
              markerEnd="url(#arrowhead-new)" 
            />
          </svg>
        </div>

        {/* Subheadline */}
        <p className="text-charcoal text-base sm:text-lg md:text-xl leading-[1.7] text-left md:text-center max-w-[900px] md:mx-auto mb-10 sm:mb-16 px-0">
          Simax Enterprise Data Warehousing connects every data source across your organization–ERP, CRM, POS, finance, HRMS, and legacy systems–into one centralized, governed platform.
        </p>
      </div>
    </section>
  );
};

export default HowSimaxSolvesSection;